import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import MainSidebar from '../../components/ui/MainSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { saleNoteService, saleNoteItemService } from '../../services/saleNoteService';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'text-warning', bg: 'bg-warning/10', icon: 'FileText' },
  en_proceso: { label: 'En Proceso', color: 'text-primary', bg: 'bg-primary/10', icon: 'Clock' },
  aprobada: { label: 'Aprobada', color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle2' },
  cancelada: { label: 'Cancelada', color: 'text-destructive', bg: 'bg-destructive/10', icon: 'XCircle' },
};

const ITEM_TYPES = [
  { value: 'service', label: 'Mano de Obra' },
  { value: 'part', label: 'Refacción' },
  { value: 'tire', label: 'Llanta' },
  { value: 'other', label: 'Otro' },
];

const emptyItem = { description: '', itemType: 'service', quantity: 1, unitPrice: 0, discountPct: 0, taxPct: 16, notes: '' };

const SaleNoteDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const printRef = useRef(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [saleNote, setSaleNote] = useState(location?.state?.saleNote || null);
  const [workOrder, setWorkOrder] = useState(location?.state?.workOrder || null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState(emptyItem);
  const [addingItem, setAddingItem] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const loadData = useCallback(async () => {
    if (!saleNote?.id && !location?.state?.saleNoteId) {
      setError('No se encontró la nota de venta. Regresa a Cotización.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const noteId = saleNote?.id || location?.state?.saleNoteId;
      // Reload sale note with items
      const fresh = await saleNoteService?.getByWorkOrderId(saleNote?.workOrderId || location?.state?.workOrderId);
      if (fresh) {
        setSaleNote(fresh);
        setItems(fresh?.items || []);
      } else {
        // Try fetching items directly
        const fetchedItems = await saleNoteItemService?.getByNoteId(noteId);
        setItems(fetchedItems || []);
      }
    } catch (err) {
      console.error('[SaleNoteDetail] Load error:', err);
      setError('Error al cargar la nota de venta. ' + (err?.message || ''));
    } finally {
      setLoading(false);
    }
  }, [saleNote?.id, saleNote?.workOrderId, location?.state?.saleNoteId, location?.state?.workOrderId, retryCount]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddItem = async () => {
    if (!newItem?.description?.trim()) {
      alert('La descripción es obligatoria');
      return;
    }
    if (!saleNote?.id) {
      alert('No se puede agregar item: nota de venta no cargada');
      return;
    }
    setAddingItem(true);
    try {
      const added = await saleNoteItemService?.addItem(saleNote?.id, saleNote?.companyId, newItem);
      setItems(prev => [...prev, added]);
      // Refresh totals
      const fresh = await saleNoteService?.getByWorkOrderId(saleNote?.workOrderId);
      if (fresh) setSaleNote(fresh);
      setNewItem(emptyItem);
      setShowAddForm(false);
      showSuccess('Item agregado correctamente');
    } catch (err) {
      console.error('[SaleNoteDetail] Add item error:', err);
      alert('Error al agregar item: ' + (err?.message || ''));
    } finally {
      setAddingItem(false);
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm('¿Eliminar este item?')) return;
    try {
      await saleNoteItemService?.removeItem(itemId, saleNote?.id);
      setItems(prev => prev?.filter(i => i?.id !== itemId));
      const fresh = await saleNoteService?.getByWorkOrderId(saleNote?.workOrderId);
      if (fresh) setSaleNote(fresh);
      showSuccess('Item eliminado');
    } catch (err) {
      alert('Error al eliminar item: ' + (err?.message || ''));
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!saleNote?.id) return;
    setSaving(true);
    try {
      const updated = await saleNoteService?.update(saleNote?.id, { status: newStatus });
      setSaleNote(prev => ({ ...prev, status: newStatus }));
      showSuccess(`Estado actualizado a: ${STATUS_CONFIG?.[newStatus]?.label}`);
    } catch (err) {
      alert('Error al actualizar estado: ' + (err?.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const statusCfg = STATUS_CONFIG?.[saleNote?.status] || STATUS_CONFIG?.draft;

  const calcItemTotal = (item) => {
    const qty = parseFloat(item?.quantity) || 1;
    const price = parseFloat(item?.unitPrice) || 0;
    const disc = parseFloat(item?.discountPct) || 0;
    const tax = parseFloat(item?.taxPct) ?? 16;
    const sub = qty * price * (1 - disc / 100);
    return sub * (1 + tax / 100);
  };

  const calcNewItemPreview = () => {
    const qty = parseFloat(newItem?.quantity) || 1;
    const price = parseFloat(newItem?.unitPrice) || 0;
    const disc = parseFloat(newItem?.discountPct) || 0;
    const tax = parseFloat(newItem?.taxPct) ?? 16;
    const sub = qty * price * (1 - disc / 100);
    return { subtotal: sub, total: sub * (1 + tax / 100) };
  };

  const preview = calcNewItemPreview();

  return (
    <>
      <Helmet>
        <title>Nota de Venta {saleNote?.folioNota || ''} - Gearcontrol</title>
        <style>{`@media print { .no-print { display: none !important; } .print-only { display: block !important; } }`}</style>
      </Helmet>
      <div className="min-h-screen bg-background">
        <div className="no-print">
          <MainSidebar isCollapsed={sidebarCollapsed} userRole="ventas" />
        </div>

        <div className={`transition-smooth no-print ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
          {/* Header */}
          <header className="sticky top-0 z-40 bg-card border-b border-border shadow-elevation-1 no-print">
            <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
              <div className="flex items-center gap-4">
                <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-smooth press-effect focus-ring">
                  <Icon name={sidebarCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={20} />
                </button>
                <button onClick={() => navigate('/quotation-management')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth">
                  <Icon name="ArrowLeft" size={20} />
                  <span className="text-sm">Volver a Cotización</span>
                </button>
                <div>
                  <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                    Nota de Venta
                  </h1>
                  <p className="text-sm caption text-muted-foreground">
                    {saleNote?.folioNota || 'Cargando...'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" iconName="Printer" iconPosition="left" onClick={handlePrint} className="no-print">
                  Imprimir / PDF
                </Button>
                {saleNote?.status === 'draft' && (
                  <Button variant="default" iconName="Send" iconPosition="left" onClick={() => handleUpdateStatus('en_proceso')} disabled={saving}>
                    Enviar al Cliente
                  </Button>
                )}
                {saleNote?.status === 'en_proceso' && (
                  <Button variant="success" iconName="CheckCircle" iconPosition="left" onClick={() => handleUpdateStatus('aprobada')} disabled={saving}>
                    Marcar Aprobada
                  </Button>
                )}
              </div>
            </div>
          </header>

          <main className="px-4 md:px-6 lg:px-8 py-6 space-y-6">
            {/* Success message */}
            {successMsg && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="CheckCircle" size={20} className="text-success" />
                <p className="text-sm text-success">{successMsg}</p>
              </div>
            )}

            {/* Error state */}
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Icon name="AlertCircle" size={20} className="text-destructive" />
                  <p className="text-sm text-destructive font-medium">{error}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" onClick={() => setRetryCount(c => c + 1)}>
                    Reintentar
                  </Button>
                  <Button variant="outline" size="sm" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate('/quotation-management')}>
                    Volver
                  </Button>
                </div>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="flex items-center justify-center py-16">
                <Icon name="Loader2" size={32} className="text-primary animate-spin" />
                <span className="ml-3 text-muted-foreground">Cargando nota de venta...</span>
              </div>
            )}

            {!loading && saleNote && (
              <>
                {/* Note header info */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${statusCfg?.bg}`}>
                        <Icon name={statusCfg?.icon} size={28} className={statusCfg?.color} />
                      </div>
                      <div>
                        <h2 className="text-2xl font-heading font-bold text-foreground">{saleNote?.folioNota}</h2>
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium mt-1 ${statusCfg?.bg} ${statusCfg?.color}`}>
                          {statusCfg?.label}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      {workOrder && (
                        <div>
                          <p className="text-muted-foreground caption">Orden de Trabajo</p>
                          <p className="font-medium text-foreground">{workOrder?.folio || workOrder?.folioOt || '-'}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-muted-foreground caption">Fecha</p>
                        <p className="font-medium text-foreground">{saleNote?.createdAt ? new Date(saleNote?.createdAt)?.toLocaleDateString('es-MX') : '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground caption">Creado por</p>
                        <p className="font-medium text-foreground">{userProfile?.fullName || 'Sistema'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items table */}
                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Icon name="List" size={22} className="text-primary" />
                      <h3 className="text-lg font-heading font-semibold text-foreground">Items de la Nota</h3>
                    </div>
                    <Button variant="default" iconName="Plus" iconPosition="left" onClick={() => setShowAddForm(true)} className="no-print">
                      Agregar Item
                    </Button>
                  </div>

                  {items?.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
                      <Icon name="PackageOpen" size={40} className="text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">Sin items. Agrega refacciones, mano de obra o llantas.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-2 font-heading font-semibold text-muted-foreground">Tipo</th>
                            <th className="text-left py-3 px-2 font-heading font-semibold text-muted-foreground">Descripción</th>
                            <th className="text-right py-3 px-2 font-heading font-semibold text-muted-foreground">Cant.</th>
                            <th className="text-right py-3 px-2 font-heading font-semibold text-muted-foreground">P. Unit.</th>
                            <th className="text-right py-3 px-2 font-heading font-semibold text-muted-foreground">Desc.%</th>
                            <th className="text-right py-3 px-2 font-heading font-semibold text-muted-foreground">IVA%</th>
                            <th className="text-right py-3 px-2 font-heading font-semibold text-muted-foreground">Total</th>
                            <th className="py-3 px-2 no-print"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {items?.map((item) => (
                            <tr key={item?.id} className="border-b border-border/50 hover:bg-muted/30 transition-smooth">
                              <td className="py-3 px-2">
                                <span className="px-2 py-0.5 rounded text-xs bg-primary/10 text-primary font-medium">
                                  {ITEM_TYPES?.find(t => t?.value === item?.itemType)?.label || item?.itemType}
                                </span>
                              </td>
                              <td className="py-3 px-2 text-foreground">{item?.description}</td>
                              <td className="py-3 px-2 text-right text-foreground">{item?.quantity}</td>
                              <td className="py-3 px-2 text-right text-foreground">${parseFloat(item?.unitPrice || 0)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                              <td className="py-3 px-2 text-right text-muted-foreground">{item?.discountPct || 0}%</td>
                              <td className="py-3 px-2 text-right text-muted-foreground">{item?.taxPct ?? 16}%</td>
                              <td className="py-3 px-2 text-right font-semibold text-foreground">${calcItemTotal(item)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                              <td className="py-3 px-2 no-print">
                                <button onClick={() => handleRemoveItem(item?.id)} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-smooth">
                                  <Icon name="Trash2" size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Totals */}
                  {items?.length > 0 && (
                    <div className="mt-6 flex justify-end">
                      <div className="w-full max-w-xs space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Subtotal</span>
                          <span className="font-medium">${parseFloat(saleNote?.subtotal || 0)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">IVA</span>
                          <span className="font-medium">${parseFloat(saleNote?.tax || 0)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between text-base font-bold border-t border-border pt-2">
                          <span className="text-foreground">Total</span>
                          <span className="text-primary">${parseFloat(saleNote?.total || 0)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Add item form */}
                {showAddForm && (
                  <div className="bg-card rounded-lg border border-primary/30 p-6 no-print">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-heading font-semibold text-foreground">Nuevo Item</h3>
                      <button onClick={() => setShowAddForm(false)} className="p-1 rounded hover:bg-muted">
                        <Icon name="X" size={20} />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="lg:col-span-2">
                        <Input
                          label="Descripción *"
                          placeholder="Ej: Cambio de pastillas de freno delanteras"
                          value={newItem?.description}
                          onChange={(e) => setNewItem(p => ({ ...p, description: e?.target?.value }))}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Tipo</label>
                        <select
                          value={newItem?.itemType}
                          onChange={(e) => setNewItem(p => ({ ...p, itemType: e?.target?.value }))}
                          className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          {ITEM_TYPES?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
                        </select>
                      </div>
                      <Input
                        label="Cantidad"
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={newItem?.quantity}
                        onChange={(e) => setNewItem(p => ({ ...p, quantity: e?.target?.value }))}
                      />
                      <Input
                        label="Precio Unitario"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={newItem?.unitPrice}
                        onChange={(e) => setNewItem(p => ({ ...p, unitPrice: e?.target?.value }))}
                      />
                      <Input
                        label="Descuento %"
                        type="number"
                        min="0"
                        max="100"
                        value={newItem?.discountPct}
                        onChange={(e) => setNewItem(p => ({ ...p, discountPct: e?.target?.value }))}
                      />
                      <Input
                        label="IVA %"
                        type="number"
                        min="0"
                        value={newItem?.taxPct}
                        onChange={(e) => setNewItem(p => ({ ...p, taxPct: e?.target?.value }))}
                      />
                      <Input
                        label="Notas"
                        placeholder="Observaciones opcionales"
                        value={newItem?.notes}
                        onChange={(e) => setNewItem(p => ({ ...p, notes: e?.target?.value }))}
                      />
                    </div>
                    <div className="mt-4 p-3 bg-muted/50 rounded-lg flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Vista previa: Subtotal ${preview?.subtotal?.toFixed(2)} | Total c/IVA ${preview?.total?.toFixed(2)}</span>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancelar</Button>
                        <Button variant="default" iconName="Plus" iconPosition="left" onClick={handleAddItem} disabled={addingItem} loading={addingItem}>
                          Agregar
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Status actions */}
                <div className="bg-card rounded-lg border border-border p-6 no-print">
                  <h3 className="text-base font-heading font-semibold text-foreground mb-4">Acciones de Estado</h3>
                  <div className="flex flex-wrap gap-3">
                    {saleNote?.status !== 'draft' && (
                      <Button variant="outline" iconName="FileText" iconPosition="left" onClick={() => handleUpdateStatus('draft')} disabled={saving}>
                        Volver a Borrador
                      </Button>
                    )}
                    {saleNote?.status === 'draft' && (
                      <Button variant="default" iconName="Send" iconPosition="left" onClick={() => handleUpdateStatus('en_proceso')} disabled={saving}>
                        Enviar al Cliente
                      </Button>
                    )}
                    {saleNote?.status === 'en_proceso' && (
                      <Button variant="success" iconName="CheckCircle" iconPosition="left" onClick={() => handleUpdateStatus('aprobada')} disabled={saving}>
                        Marcar como Aprobada
                      </Button>
                    )}
                    {saleNote?.status !== 'cancelada' && (
                      <Button variant="destructive" iconName="XCircle" iconPosition="left" onClick={() => handleUpdateStatus('cancelada')} disabled={saving}>
                        Cancelar Nota
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </main>
        </div>

        {/* Print view */}
        <div className="hidden print:block p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold">NOTA DE VENTA</h1>
            <p className="text-xl mt-2">{saleNote?.folioNota}</p>
            <p className="text-sm text-gray-600 mt-1">Fecha: {saleNote?.createdAt ? new Date(saleNote?.createdAt)?.toLocaleDateString('es-MX') : '-'}</p>
          </div>
          {workOrder && (
            <div className="mb-6">
              <p><strong>Orden de Trabajo:</strong> {workOrder?.folio || workOrder?.folioOt}</p>
              <p><strong>Vehículo:</strong> {workOrder?.vehicle}</p>
              <p><strong>Cliente:</strong> {workOrder?.client}</p>
            </div>
          )}
          <table className="w-full border-collapse border border-gray-300 text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-2 text-left">Descripción</th>
                <th className="border border-gray-300 p-2 text-right">Cant.</th>
                <th className="border border-gray-300 p-2 text-right">P. Unit.</th>
                <th className="border border-gray-300 p-2 text-right">IVA%</th>
                <th className="border border-gray-300 p-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item?.id}>
                  <td className="border border-gray-300 p-2">{item?.description}</td>
                  <td className="border border-gray-300 p-2 text-right">{item?.quantity}</td>
                  <td className="border border-gray-300 p-2 text-right">${parseFloat(item?.unitPrice || 0)?.toFixed(2)}</td>
                  <td className="border border-gray-300 p-2 text-right">{item?.taxPct ?? 16}%</td>
                  <td className="border border-gray-300 p-2 text-right">${calcItemTotal(item)?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 text-right">
            <p>Subtotal: ${parseFloat(saleNote?.subtotal || 0)?.toFixed(2)}</p>
            <p>IVA: ${parseFloat(saleNote?.tax || 0)?.toFixed(2)}</p>
            <p className="text-xl font-bold">TOTAL: ${parseFloat(saleNote?.total || 0)?.toFixed(2)}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SaleNoteDetail;
